import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useMemo, useRef } from 'react'

import { TempWorkerPaymentSlipDocument } from '@/components/pages/Admin/TempWorkers/TempWorkerPaymentSlipDocument'
import { Button } from '@/components/ui'
import { useLocale, useTranslations } from '@/hooks/useTranslations'
import {
  companyProfileForSlip,
  useCompanyProfiles,
} from '@/modules/company-profile/services/companyProfile'
import { useTempWorkerPaymentSlip } from '@/modules/temp-worker/services/tempWorker'

export const Route = createFileRoute('/(dashboard)/admin/temp-workers/$id/slip/$txnId')({
  component: TempWorkerPaymentSlipPage,
})

function TempWorkerPaymentSlipPage() {
  const { id, txnId } = Route.useParams()
  const t = useTranslations('temp_worker_settlement')
  const locale = useLocale()
  const printedRef = useRef(false)

  const { data: companyRes } = useCompanyProfiles()
  const { data: slipRes, isLoading, isError } = useTempWorkerPaymentSlip(id, txnId)

  const company = slipRes?.data
    ? companyProfileForSlip(companyRes?.data, slipRes.data)
    : undefined

  const labels = useMemo(
    () => ({
      slip_settlement_title: t('slip_settlement_title'),
      slip_advance_title: t('slip_advance_title'),
      worker_name: t('slip_worker_name'),
      worker_position: t('slip_worker_position'),
      worker_phone: t('slip_worker_phone'),
      work_period: t('slip_work_period'),
      col_date: t('slip_col_date'),
      col_location: t('slip_col_location'),
      col_hours: t('slip_col_hours'),
      col_pay: t('slip_col_pay'),
      col_note: t('txn_note'),
      gross_wage: t('gross_wage'),
      credit_applied: t('credit_applied'),
      net_cash: t('net_cash'),
      remaining_credit: t('remaining_credit'),
      total_row: t('slip_total_row'),
      paid_at: t('slip_paid_at'),
      advance_amount: t('slip_advance_amount'),
      note: t('txn_note'),
      signature_receiver: t('slip_signature_receiver'),
      signature_payer: t('slip_signature_payer'),
      baht: t('baht'),
    }),
    [t],
  )

  useEffect(() => {
    document.body.classList.add('printing-temp-worker-slip')
    return () => {
      document.body.classList.remove('printing-temp-worker-slip')
    }
  }, [])

  useEffect(() => {
    if (isLoading || isError || !slipRes?.data || printedRef.current) return
    printedRef.current = true
    const timer = window.setTimeout(() => {
      window.print()
    }, 400)
    return () => window.clearTimeout(timer)
  }, [isLoading, isError, slipRes?.data])

  if (isLoading) {
    return <p className="p-8 text-sm text-muted-foreground">{t('loading')}</p>
  }

  if (isError || !slipRes?.data) {
    return (
      <div className="p-8">
        <p className="text-sm text-destructive">{t('slip_load_fail')}</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/admin/temp-workers/$id" params={{ id }}>
            {t('slip_back')}
          </Link>
        </Button>
      </div>
    )
  }

  return (
    <>
      <style>{`
        @page {
          size: A4 portrait;
          margin: 12mm;
        }
        @media print {
          html,
          body {
            height: auto !important;
            overflow: visible !important;
            background: #fff !important;
          }
          /* Unlock admin layout so tall slips can paginate past page 1 */
          body.printing-temp-worker-slip [class*="sidebar-wrapper"],
          body.printing-temp-worker-slip main,
          body.printing-temp-worker-slip main > div,
          body.printing-temp-worker-slip .app-content-scale,
          body.printing-temp-worker-slip .slip-print-page {
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }
          body.printing-temp-worker-slip .app-content-scale {
            zoom: 1 !important;
          }
          body.printing-temp-worker-slip * {
            visibility: hidden;
          }
          body.printing-temp-worker-slip #slip-print-root,
          body.printing-temp-worker-slip #slip-print-root * {
            visibility: visible;
          }
          body.printing-temp-worker-slip #slip-print-root {
            position: static;
            width: 100%;
            max-width: 100%;
            margin: 0;
            padding: 0;
            box-shadow: none;
            background: #fff;
            color: #111;
          }
        }
      `}</style>

      <div className="slip-print-page mx-auto max-w-[210mm] print:max-w-none">
        <div className="slip-screen-toolbar mb-4 flex flex-wrap gap-2 print:hidden">
          <Button type="button" variant="outline" onClick={() => window.print()}>
            {t('slip_print_again')}
          </Button>
          <Button asChild variant="ghost">
            <Link to="/admin/temp-workers/$id" params={{ id }}>
              {t('slip_back')}
            </Link>
          </Button>
        </div>

        <TempWorkerPaymentSlipDocument
          company={company}
          slip={slipRes.data}
          labels={labels}
          locale={locale}
        />
      </div>
    </>
  )
}
